import React, { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Combobox } from '@headlessui/react';
import { CheckIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';

const AddCalendarDialog = ({ isOpen, onClose, onSubmit, categories = [], userRole = null }) => {
  const [calendarName, setCalendarName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryQuery, setCategoryQuery] = useState('');
  // Try to get accountNo from the current URL
  const [accountNo, setAccountNo] = useState('');
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const acc = urlParams.get('acc') || urlParams.get('accountNo');
      if (acc) setAccountNo(acc);
    } catch (e) {
      setAccountNo('');
    }
  }, []);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setCalendarName('');
      setSelectedCategory(null);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit({
      name: calendarName,
      categoryId: selectedCategory?._id || null,
      categoryName: selectedCategory?.name || null
    });
    setCalendarName('');
    setSelectedCategory(null);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-4"
          >
            <div className="relative p-0">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                aria-label="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            
            {/* Header with border-bottom */}
            <div className="border-b border-gray-200 py-4 mb-8">
              <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
                Add New Calendar
              </DialogTitle>
            </div>

            {/* Main content */}
            <div className="mt-8 px-4">
              <div className="flex items-center space-x-4">
                <label
                  htmlFor="calendarName"
                  className="block text-sm font-medium text-gray-900 w-1/3"
                >
                  Calendar Name
                </label>
                <div className="flex-grow">
                  <input
                    id="calendarName"
                    name="calendarName"
                    type="text"
                    placeholder="Enter calendar name"
                    value={calendarName}
                    onChange={(e) => setCalendarName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && calendarName.trim()) {
                        handleSubmit();
                      }
                    }}
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Category Combobox */}
            <div className="mt-6 px-4">
              <div className="flex items-center space-x-4">
                <label
                  htmlFor="category-combobox"
                  className="block text-sm font-medium text-gray-900 w-1/3 self-start pt-2"
                >
                  Category
                </label>
                <div className="flex-grow">
                  <Combobox
                    as="div"
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 shadow-sm focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        displayValue={cat => cat ? cat.name : ''}
                        placeholder="Select a category"
                        onChange={e => setCategoryQuery(e.target.value)}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {categories.length === 0 && (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No categories found
                          </div>
                        )}
                        {categories
                          .filter(cat =>
                            !categoryQuery || cat.name.toLowerCase().includes(categoryQuery.toLowerCase())
                          )
                          .map(cat => (
                            <Combobox.Option
                              key={cat._id}
                              value={cat}
                              className={({ active, selected }) =>
                                [
                                  'relative cursor-pointer select-none py-2 px-4',
                                  active ? 'bg-black text-white rounded-md' : 'text-gray-900',
                                  selected ? 'font-semibold' : 'font-normal'
                                ].join(' ')
                              }
                            >
                              {({ selected, active }) => (
                                <span
                                  className={[
                                    'block truncate',
                                    selected ? 'font-semibold' : 'font-normal',
                                    active ? 'text-white' : 'text-gray-900'
                                  ].join(' ')}
                                >
                                  {cat.name}
                                </span>
                              )}
                            </Combobox.Option>
                          ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                  {/* Add Category link - hidden for admin users */}
                  {userRole !== 'admin' && userRole !== 'Admin' && userRole !== 1 && (
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
                        onClick={() => {
                          if (window && window.dispatchEvent) {
                            window.dispatchEvent(new CustomEvent('openGoogleCalendarCategoryTab'));
                          }
                          // Delay onClose to ensure event is handled before dialog closes
                          if (onClose) setTimeout(onClose, 100);
                        }}
                      >
                        <PlusCircleIcon className="h-4 w-4 mr-1" />
                        Add Category
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Button Section with top border */}
            <div className="border-t border-gray-200 mt-10">
              <div className="flex justify-between mt-5">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md bg-gray-200 text-gray-700 py-2 px-4 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!calendarName.trim() || !selectedCategory}
                  className={`rounded-md ${
                    !calendarName.trim() || !selectedCategory ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"
                  } text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                >
                  Create
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default AddCalendarDialog;
